import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Space,
  Typography,
  Modal,
  Form,
  message,
} from 'antd';
import {
  PlusOutlined,
  BarChartOutlined,
  ExperimentOutlined,
  BookOutlined,
} from '@ant-design/icons';

// Import organized components
import AdaptationList from './components/AdaptationList';
import AdaptationForm from './components/AdaptationForm';
import AdaptationTabs from './components/AdaptationTabs';
import StatsCard from '../components/shared/StatsCard';

// Import data and utilities
import { mockAdaptations, mockCategoryProgress } from './data/mockData';
import { calculateAdaptationStats, searchAdaptations } from './utils/adaptationUtils';
import { Adaptation } from '../types';

const { Title } = Typography;

const AdaptationsLibrary: React.FC = () => {
  const [adaptations, setAdaptations] = useState<Adaptation[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAdaptation, setEditingAdaptation] = useState<Adaptation | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [form] = Form.useForm();

  // Load mock data
  useEffect(() => {
    setAdaptations(mockAdaptations);
  }, []);

  const handleAdd = () => {
    setEditingAdaptation(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (adaptation: Adaptation) => {
    setEditingAdaptation(adaptation);
    form.setFieldsValue({
      ...adaptation,
      scientificParameters: adaptation.scientificParameters.join('\n'),
      exerciseAssociations: adaptation.exerciseAssociations.join(', '),
    });
    setIsModalVisible(true);
  };

  const handleDelete = (adaptation: Adaptation) => {
    Modal.confirm({
      title: 'Delete Adaptation',
      content: `Are you sure you want to delete "${adaptation.name}"?`,
      onOk: () => {
        setAdaptations(prev => prev.filter(a => a.id !== adaptation.id));
        message.success('Adaptation deleted successfully');
      },
    });
  };

  const handleSave = async (values: any) => {
    try {
      if (editingAdaptation) {
        setAdaptations(prev => prev.map(a => 
          a.id === editingAdaptation.id ? { ...a, ...values } : a
        ));
        message.success('Adaptation updated successfully');
      } else {
        const newAdaptation: Adaptation = {
          id: Date.now().toString(),
          ...values,
        };
        setAdaptations(prev => [...prev, newAdaptation]);
        message.success('Adaptation added successfully');
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Failed to save adaptation');
    }
  };

  const getFilteredAdaptations = () => {
    if (selectedCategory === 'all') return adaptations;
    return adaptations.filter(a => a.category === selectedCategory);
  };

  return (
    <div>
      <Row gutter={[24, 24]}>
        {/* Header */}
        <Col span={24}>
          <Card>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={2} style={{ margin: 0 }}>
                  <ExperimentOutlined /> Adaptations Library
                </Title>
                <Typography.Text type="secondary">
                  Training adaptations configuration and scientific parameters
                </Typography.Text>
              </Col>
              <Col>
                <Space>
                  <Button icon={<BookOutlined />}>
                    Scientific References
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    onClick={handleAdd}
                  >
                    Add Adaptation
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Statistics */}
        <Col span={24}>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <StatsCard
                title="Overview"
                icon={<BarChartOutlined />}
                stats={calculateAdaptationStats(adaptations)}
                size="small"
              />
            </Col>
            <Col xs={24} lg={8}>
              <StatsCard
                title="Category Distribution"
                progress={mockCategoryProgress}
                size="small"
              />
            </Col>
          </Row>
        </Col>

        {/* Category Tabs and Adaptations List */}
        <Col span={24}>
          <Card>
            <AdaptationTabs
              activeTab={selectedCategory}
              onChange={setSelectedCategory}
            >
              <AdaptationList
                adaptations={getFilteredAdaptations()}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </AdaptationTabs>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Modal */}
      <Modal
        title={editingAdaptation ? 'Edit Adaptation' : 'Add New Adaptation'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        <AdaptationForm
          form={form}
          adaptation={editingAdaptation}
          onSave={handleSave}
          onCancel={() => setIsModalVisible(false)}
        />
      </Modal>
    </div>
  );
};

export default AdaptationsLibrary; 